import { parseLocale } from "@/i18n";
import { type CheckoutSession, type Store } from "@/types";
import { getRouteForSession } from "@/modules/checkout-session/hooks/use-checkout-redirect";
import { type ExternalVerificationConfig } from "./types";

const verifyRequestByKey = new Map<string, Promise<CheckoutSession>>();

/**
 * Deduplicate concurrent verify requests for the same external step.
 * React StrictMode remounts and overlapping effects otherwise fire duplicate
 * POSTs; the backend accepts the first and returns 409 on the second.
 */
export function deduplicatedVerifyRequest(
  key: string,
  request: () => Promise<CheckoutSession>,
): Promise<CheckoutSession> {
  const existing = verifyRequestByKey.get(key);
  if (existing) {
    return existing;
  }

  const promise = request().finally(() => {
    verifyRequestByKey.delete(key);
  });

  verifyRequestByKey.set(key, promise);
  return promise;
}

export function clearVerifyRequestDedup(key: string): void {
  verifyRequestByKey.delete(key);
}

/** True when the session has already left the await-* external verification state. */
export function isVerificationAlreadyCompleted(
  session: CheckoutSession,
  config: ExternalVerificationConfig,
): boolean {
  return session.status !== config.awaitStatus;
}

/**
 * Decision for what the external-verification return page should do once a
 * `verify-*` call resolves.
 *
 * - `pending-resume`: still awaiting this step and a redirection URL is
 *   available. Show the pending UI so the customer can resume the external
 *   IDV/questionnaire or leave to the merchant.
 * - `await-incomplete`: still awaiting this step but no redirection URL — show
 *   the hard-error retry state (cannot resume externally).
 * - `resume`: continue into the app at `route`.
 */
export type PostVerifyOutcome =
  | { type: "pending-resume"; redirectUrl: string }
  | { type: "await-incomplete" }
  | { type: "resume"; route: string };

/**
 * Resolves the post-verify action for KYC / questionnaire return.
 *
 * Falls back to `/` when the resolved status has no explicit route mapping
 * (e.g. a credit decline lands the session in `FAILED`, which maps to `""`).
 * Routing to `/` lets the home `AppWrapper` render the decline or failed screen
 * instead of leaving the user on the blank return page.
 */
export function getPostVerifyOutcome(
  session: CheckoutSession,
  config: ExternalVerificationConfig,
): PostVerifyOutcome {
  if (session.status === config.awaitStatus) {
    const redirectUrl = session.redirectUrl?.trim();
    if (redirectUrl) {
      return { type: "pending-resume", redirectUrl };
    }
    return { type: "await-incomplete" };
  }
  return { type: "resume", route: getRouteForSession(session) || "/" };
}

export function buildExternalVerificationReturnUrl(
  siteUrl: string,
  checkoutId: string,
  returnPath: string,
  locale?: string,
): string {
  const base = siteUrl.replace(/\/$/, "");
  const url = new URL(`${base}${returnPath}`);
  url.searchParams.set("checkoutId", checkoutId);
  if (locale) {
    url.searchParams.set("locale", locale);
  }
  return url.toString();
}

/** Apply PPUI locale to an outbound COS redirect URL (questionnaire / KYC). */
export function applyLocaleToExternalRedirectUrl(
  redirectUrl: string,
  language: string,
): string {
  const locale = parseLocale(language) ?? "en_US";

  try {
    const url = new URL(redirectUrl);
    url.searchParams.set("locale", locale);

    const callbackRedirectUrl = url.searchParams.get("callback_redirect_url");
    if (callbackRedirectUrl) {
      const callbackUrl = new URL(callbackRedirectUrl);
      callbackUrl.searchParams.set("locale", locale);
      url.searchParams.set("callback_redirect_url", callbackUrl.toString());
    }

    return url.toString();
  } catch {
    return redirectUrl;
  }
}

export const setRedirectInitiated = async (
  store: Store,
  config: ExternalVerificationConfig,
): Promise<void> => {
  await store.set(config.storageKey, "true");
};

export const isRedirectInitiated = async (
  store: Store,
  config: ExternalVerificationConfig,
): Promise<boolean> => {
  const value = await store.get(config.storageKey);
  return value === "true";
};

export const clearRedirectInitiated = async (
  store: Store,
  config: ExternalVerificationConfig,
): Promise<void> => {
  await store.set(config.storageKey, "");
};

export const redirectSessionKey = (
  config: ExternalVerificationConfig,
  sessionId: string,
): string => `${config.sessionStorageKeyPrefix}_${sessionId}`;
